import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelPageProps } from "src/web/interfaces";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import {
    Box,
    StatusIndicator,
    Button,
    Header,
    Link,
    Pagination,
    SpaceBetween,
    Table,
    TextFilter,
    Alert,
} from "@cloudscape-design/components";
import { getPipelinePackages } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { PipelinePackagesResponse, TeamPackage } from "src/core/sentinel/types";
import { useParams, useNavigate } from "react-router-dom";
export interface PipelinePackagesPageProps extends SentinelPageProps {
  readOnly?: boolean;
  sentinelController: SentinelController;
}
export const PipelinePackagesPage: React.FC<PipelinePackagesPageProps> = (
    props: PipelinePackagesPageProps
) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filteringText, setFilteringText] = useState("");
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [packages, setPackages] = useState<TeamPackage[]>([]);
    const pageSize = 50;
    const { teamId, pipelineName } = useParams<{ teamId: string; pipelineName: string }>();
    useEffect(() => {
        if (!pipelineName) return;
        const fetchPackages = async () => {
            try {
                const response = await dispatch(
                    getPipelinePackages({
                        sentinelController: props.sentinelController,
                        pipelineName: pipelineName,
                    })
                ).unwrap();
                const pkgList = (response as any)?.payload?.packages;
                setPackages(pkgList || []);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                setError("Failed to load packages for this pipeline.");
            }
        };
        fetchPackages();
    }, [pipelineName]);

    const filteredPackages = packages.filter((p) => {
        const content = [p.packageName, p.packageMajorVersion, p.versionSet]
            .join(" ")
            .toLowerCase();
        return !filteringText || content.includes(filteringText.toLowerCase());
    });

    const paginatedItems = filteredPackages.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );

    const totalPages = Math.ceil(filteredPackages.length / pageSize);

    useEffect(() => {
        dispatch(setPageTitle("Pipeline Packages"));
        dispatch(removeInfoMessage({ id: "message_1" }));
    }, []);

    if (!pipelineName) {
        return (
            <SentinelBasePage>
                {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
                <Box textAlign="center" padding="xl">
                    <StatusIndicator type="error">
                        No pipeline specified. Please navigate from the Teams page.
                    </StatusIndicator>
                </Box>
            </SentinelBasePage>
        );
    }

    return (
        <SentinelBasePage>
            {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
            <Table
                items={paginatedItems}
                columnDefinitions={[
                    {
                        id: "packageName",
                        header: "Package Name",
                        cell: (e: TeamPackage) => (
                            <Link href={`#/team/details/${teamId}/${e.packageName}`}>
                                {e.packageName}
                            </Link>
                        ),
                        sortingField: "packageName",
                        isRowHeader: true,
                    },
                    {
                        id: "packageMajorVersion",
                        header: "Major Version",
                        cell: (e: TeamPackage) => e.packageMajorVersion,
                        sortingField: "packageMajorVersion",
                    },
                    {
                        id: "versionSet",
                        header: "Version Set",
                        cell: (e: TeamPackage) => e.versionSet,
                    },
                    {
                        id: "packageBranch",
                        header: "Branch",
                        cell: (e: TeamPackage) => e.packageBranch,
                    },
                ]}
                renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
                    `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
                }
                loading={loading}
                loadingText="Loading packages"
                empty={
                    <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                        <SpaceBetween size="m">
                            <b>No packages found</b>
                        </SpaceBetween>
                    </Box>
                }
                filter={
                    <TextFilter
                        filteringPlaceholder="Filter packages"
                        filteringText={filteringText}
                        onChange={({ detail }) => {
                            setFilteringText(detail.filteringText);
                            setCurrentPageIndex(1);
                        }}
                        countText={`${filteredPackages.length} matches`}
                    />
                }
                header={
                    <Header
                        actions={
                            <Button variant="link" onClick={() => navigate(`/team/details/${teamId}`)}>
                ← Back to Pipelines
                            </Button>
                        }
                    >
            Packages in Pipeline: {pipelineName}
                    </Header>
                }
                pagination={
                    <Pagination
                        currentPageIndex={currentPageIndex}
                        pagesCount={totalPages}
                        onChange={({ detail }) =>
                            setCurrentPageIndex(detail.currentPageIndex)
                        }
                    />
                }
            />
        </SentinelBasePage>
    );
};
